import { Check, TriangleAlert } from 'lucide-react'
import { useSyncExternalStore } from 'react'

// Одно короткое сообщение поверх всего: «Ссылка скопирована». Новое заменяет старое.
// С действием («Добавлено в «Идея» · Открыть», «Карточка удалена · Вернуть») тост
// живёт 4 с и ловит нажатия — он единственный с pointer-events.

export interface ToastAction {
  label: string
  onClick: () => void
}

interface ToastState {
  id: number
  text: string
  error: boolean
  action: ToastAction | null
}

interface ToastOptions {
  error?: boolean
  ms?: number
  action?: ToastAction
}

let current: ToastState | null = null
let timer: ReturnType<typeof setTimeout> | null = null
let seq = 0
const listeners = new Set<() => void>()

function emit() {
  listeners.forEach((cb) => cb())
}

function dismiss() {
  if (timer) clearTimeout(timer)
  timer = null
  current = null
  emit()
}

export function toast(text: string, { error = false, ms, action }: ToastOptions = {}): void {
  seq += 1
  current = { id: seq, text, error, action: action ?? null }
  if (timer) clearTimeout(timer)
  timer = setTimeout(dismiss, ms ?? (action ? 4000 : 2200))
  emit()
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

/** Ставится один раз в корне приложения. */
export function Toaster() {
  const state = useSyncExternalStore(subscribe, () => current)
  const Icon = state?.error ? TriangleAlert : Check
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 top-[calc(var(--safe-top)+3.75rem)] z-[60] flex justify-center px-4 desk:top-4 desk:right-4 desk:left-auto desk:justify-end"
    >
      {state && (
        <div
          key={state.id}
          role="status"
          className={`inline-flex max-w-full items-center gap-2 rounded-xl py-2.5 pl-3.5 text-sm font-medium shadow-lg motion-safe:animate-[toast-in_160ms_ease-out] ${
            state.action ? 'pointer-events-auto pr-1.5' : 'pr-3.5'
          } ${state.error ? 'bg-danger text-white' : 'bg-accent text-accent-fg'}`}
        >
          <Icon size={16} className="shrink-0" />
          <span className="min-w-0">{state.text}</span>
          {state.action && (
            <button
              type="button"
              onClick={() => {
                const action = state.action
                dismiss()
                action?.onClick()
              }}
              className="-my-1 ml-1 inline-flex h-8 shrink-0 items-center rounded-lg px-2.5 font-semibold underline-offset-2 hover:underline active:opacity-70"
            >
              {state.action.label}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
